/**
 * studio-shop D4 — the page's shop: which product's window is open (with the
 * grid it was opened from, for the window's previous and next) and whether the
 * bag's drawer is open. A custom page places the grid, the window and the bag
 * as placeholders of their own, so this is what ties them together: the grid
 * opens a product here, the window shows it, and the bag button opens the drawer
 * the page places, or the bag page when it places none. The kit keeps one per
 * page, made new each time it starts; never one for the whole browser.
 */
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';

export interface ShopPageState {
  /** The product whose window is open, and the grid's products in order; null while none is. */
  readonly product: { readonly handle: string; readonly handles: readonly string[] } | null;
  /** The bag's drawer is open. */
  readonly bagOpen: boolean;
}

export class ShopPage {
  private state: ShopPageState = { product: null, bagOpen: false };
  private readonly listeners = new Set<() => void>();
  /** How many windows and bag drawers the page places now. */
  private readonly placed = { windows: 0, drawers: 0 };

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly get = (): ShopPageState => this.state;

  private set(next: Partial<ShopPageState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) listener();
  }

  /** Counts a placed window or drawer while it is on the page; returns its removal. */
  place(kind: 'windows' | 'drawers'): () => void {
    this.placed[kind] += 1;
    return () => {
      this.placed[kind] -= 1;
    };
  }

  hasWindow(): boolean {
    return this.placed.windows > 0;
  }

  hasDrawer(): boolean {
    return this.placed.drawers > 0;
  }

  private warned = false;

  /** A grid opened a product on a page that places no window: says so once for the page. */
  warnNoWindow(): void {
    if (this.warned || this.hasWindow()) return;
    this.warned = true;
    console.warn('[telify] shop-grid: this page places no shop-product, so a product has nowhere to open.');
  }

  openProduct(handle: string, handles: readonly string[]): void {
    this.set({ product: { handle, handles }, bagOpen: false });
  }

  /** Steps the open window to the previous (-1) or next (1) product of its grid, round the end. */
  step(by: -1 | 1): void {
    const product = this.state.product;
    if (!product || product.handles.length < 2) return;
    const at = product.handles.indexOf(product.handle);
    const next = product.handles[(at + by + product.handles.length) % product.handles.length]!;
    this.set({ product: { ...product, handle: next } });
  }

  closeProduct(): void {
    if (this.state.product) this.set({ product: null });
  }

  openBag(): void {
    this.set({ product: null, bagOpen: true });
  }

  closeBag(): void {
    if (this.state.bagOpen) this.set({ bagOpen: false });
  }
}

const ShopPageContext = createContext<ShopPage | null>(null);

/** Set by the kit around a page's placements, a new one each time the kit starts, so each page starts with nothing open. */
export const ShopPageProvider = ShopPageContext.Provider;

/** The page's shop and what is open on it; a piece outside the kit's placements has one of its own, shared with nothing. */
export function useShopPage(): { readonly page: ShopPage; readonly state: ShopPageState } {
  const shared = useContext(ShopPageContext);
  const [own] = useState(() => new ShopPage());
  const page = shared ?? own;
  const state = useSyncExternalStore(page.subscribe, page.get);
  return { page, state };
}

/** Counts this window or drawer as placed on the page while it is mounted. */
export function usePlaceOnShopPage(page: ShopPage, kind: 'windows' | 'drawers', placed: boolean): void {
  useEffect(() => (placed ? page.place(kind) : undefined), [page, kind, placed]);
}
